import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { downloadXlsx } from "@/lib/download";
import { X, Loader2, FileSpreadsheet, FileText, ArrowRightLeft, Trophy, Search, AlertTriangle, Truck } from "lucide-react";
import { toast } from "sonner";
import BulkTransferCascadeModal from "./BulkTransferCascadeModal";

const fmtEur = (n) =>
  `${(Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

/**
 * Modal "Comparar 2 laboratorios" (iter-71).
 * Elige lab_a y lab_b y muestra KPIs de cada uno, moléculas comunes y
 * exclusivas, con recomendación de ahorro €/mes y descarga Excel/PDF.
 */
export default function LabComparisonModal({ dispId, onClose }) {
  const [labs, setLabs] = useState([]);
  const [loadingLabs, setLoadingLabs] = useState(true);
  const [labA, setLabA] = useState("");
  const [labB, setLabB] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [onlyPurchased, setOnlyPurchased] = useState(true);

  useEffect(() => {
    setLoadingLabs(true);
    // Reusamos supplier-economics del estudio: devuelve todos los labs relevantes.
    api
      .get(`/dispensations/${dispId}/supplier-economics`)
      .then((r) => {
        const list = (r.data?.suppliers || r.data || [])
          .map((s) => s.laboratorio)
          .filter(Boolean);
        setLabs([...new Set(list)].sort((a, b) => a.localeCompare(b, "es")));
      })
      .catch(() => toast.error("No se pudieron cargar los laboratorios"))
      .finally(() => setLoadingLabs(false));
  }, [dispId]);

  useEffect(() => {
    if (!labA || !labB || labA === labB) {
      setData(null);
      return;
    }
    setLoading(true);
    setErr(null);
    api
      .get(`/dispensations/${dispId}/lab-comparison`, {
        params: { lab_a: labA, lab_b: labB },
      })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Error en la comparativa"))
      .finally(() => setLoading(false));
  }, [dispId, labA, labB]);

  const canDownload = data && labA && labB && labA !== labB;

  // Ganador global de la comparativa → origen (perdedor) y destino (ganador)
  // para el traslado masivo, restringido a las moléculas que gana.
  const transferPlan = useMemo(() => {
    const w = data?.totals?.overall_winner;
    if (!w) return null;
    const aIsWinner = data.lab_a?.laboratorio === w;
    const winnerLab = aIsWinner ? labA : labB;
    const loserLab = aIsWinner ? labB : labA;
    const wonGhs = (data.common || []).filter((r) => r.winner === w).map((r) => r.gh);
    const exclusive = (aIsWinner ? data.only_a : data.only_b) || [];
    if (!winnerLab || !loserLab || wonGhs.length === 0) return null;
    return {
      winnerLab, loserLab, wonGhs,
      newEntriesGhs: exclusive.map((r) => r.gh),
      savingMonthEur: aIsWinner
        ? data.totals?.wins_a_saving_month_eur
        : data.totals?.wins_b_saving_month_eur,
    };
  }, [data, labA, labB]);

  const filteredCommon = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    let rows = data.common || [];
    if (onlyPurchased) rows = rows.filter((r) => (r.units_dispensed || 0) > 0);
    if (q) rows = rows.filter((r) => r.gh.toLowerCase().includes(q));
    return rows;
  }, [data, query, onlyPurchased]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-stretch" data-testid="lab-comparison-modal">
      <div className="bg-white w-full h-full flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-700 font-bold mb-0.5">Análisis · Rentabilidad</div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-950">
              Comparar dos laboratorios
            </h2>
            <div className="text-xs text-slate-500 mt-0.5">
              KPIs cara a cara, moléculas comunes y exclusivas, y ahorro €/mes potencial.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={!transferPlan}
              onClick={() => transferPlan && setTransferOpen(true)}
              title={transferPlan
                ? `Trasladar las ${transferPlan.wonGhs.length} moléculas que gana ${transferPlan.winnerLab} desde ${transferPlan.loserLab}`
                : "Elige dos laboratorios con un ganador claro para poder trasladar"}
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold px-3.5 py-2 rounded-lg"
              data-testid="lab-comparison-apply-transfer"
            >
              <Truck className="h-4 w-4" />
              {transferPlan ? `Trasladar al ganador (${transferPlan.winnerLab})` : "Trasladar al ganador"}
            </button>
            <button
              disabled={!canDownload}
              onClick={() =>
                canDownload &&
                downloadXlsx(
                  `/dispensations/${dispId}/lab-comparison?lab_a=${encodeURIComponent(labA)}&lab_b=${encodeURIComponent(labB)}&export=xlsx`,
                  `comparativa_${labA}_vs_${labB}.xlsx`,
                )
              }
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold px-3.5 py-2 rounded-lg"
              data-testid="lab-comparison-download-xlsx"
            >
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button
              disabled={!canDownload}
              onClick={() =>
                canDownload &&
                downloadXlsx(
                  `/dispensations/${dispId}/lab-comparison?lab_a=${encodeURIComponent(labA)}&lab_b=${encodeURIComponent(labB)}&export=pdf`,
                  `comparativa_${labA}_vs_${labB}.pdf`,
                )
              }
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold px-3.5 py-2 rounded-lg"
              data-testid="lab-comparison-download-pdf"
            >
              <FileText className="h-4 w-4" /> PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
              data-testid="lab-comparison-close"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Selectors */}
        <div className="border-b border-slate-200 px-6 py-4 flex-shrink-0 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center bg-slate-50">
          <LabSelect
            label="Laboratorio A"
            value={labA}
            onChange={setLabA}
            labs={labs}
            excluded={labB}
            disabled={loadingLabs}
            testid="lab-comparison-select-a"
          />
          <div className="hidden md:flex justify-center items-center text-slate-400">
            <ArrowRightLeft className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <LabSelect
            label="Laboratorio B"
            value={labB}
            onChange={setLabB}
            labs={labs}
            excluded={labA}
            disabled={loadingLabs}
            testid="lab-comparison-select-b"
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {(!labA || !labB) && (
            <div className="p-12 text-center text-slate-400" data-testid="lab-comparison-empty">
              Selecciona dos laboratorios para ver la comparativa.
            </div>
          )}
          {labA && labB && labA === labB && (
            <div className="p-12 text-center text-rose-500">Elige laboratorios distintos.</div>
          )}
          {loading && (
            <div className="p-12 text-center text-slate-500 inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="h-4 w-4 animate-spin" /> Comparando…
            </div>
          )}
          {err && <div className="p-12 text-center text-rose-600" data-testid="lab-comparison-error">{err}</div>}
          {data && !loading && (
            <div className="p-6 space-y-6">
              {/* Recomendación */}
              <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 flex items-start gap-4">
                <Trophy className="h-8 w-8 text-emerald-600 flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-800 mb-1">
                    Recomendación
                  </div>
                  <div className="text-lg text-slate-900 font-semibold">
                    {data.totals.overall_winner ? (
                      <>Comprando cada molécula al mejor laboratorio ahorrarías <span className="text-emerald-700">{fmtEur(data.totals.potential_saving_month_eur)}/mes</span> (<span className="text-emerald-700">{fmtEur(data.totals.potential_saving_year_eur)}/año</span>).</>
                    ) : (
                      <>Ambos laboratorios rinden por igual: ahorro potencial total {fmtEur(data.totals.potential_saving_month_eur)}/mes repartido.</>
                    )}
                  </div>
                  {data.totals.potential_saving_month_with_rappel_eur > data.totals.potential_saving_month_eur && (
                    <div className="text-sm text-amber-800 mt-1 bg-amber-50 border border-amber-200 px-2 py-1 rounded inline-block">
                      Con rappel activado: <strong>{fmtEur(data.totals.potential_saving_month_with_rappel_eur)}/mes</strong> ({fmtEur(data.totals.potential_saving_year_with_rappel_eur)}/año).
                    </div>
                  )}
                  {data.totals.overall_winner && (
                    <div className="text-sm text-slate-600 mt-1">
                      Laboratorio global más rentable: <strong className="text-slate-900">{data.totals.overall_winner}</strong>. {data.lab_a.laboratorio} aporta {fmtEur(data.totals.wins_a_saving_month_eur)}/mes; {data.lab_b.laboratorio} aporta {fmtEur(data.totals.wins_b_saving_month_eur)}/mes.
                    </div>
                  )}
                </div>
              </div>

              {/* KPIs por lab */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <LabKpiCard k={data.lab_a} winners={data.totals.wins_a_saving_month_eur} months={data.months} highlight={data.totals.overall_winner === data.lab_a.laboratorio} />
                <LabKpiCard k={data.lab_b} winners={data.totals.wins_b_saving_month_eur} months={data.months} highlight={data.totals.overall_winner === data.lab_b.laboratorio} />
              </div>

              {/* Distribución de moléculas */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MiniCard color="slate" label="Comunes" value={data.totals.n_common} />
                <MiniCard color="blue" label={`Sólo ${data.lab_a.laboratorio}`} value={data.totals.n_only_a} />
                <MiniCard color="pink" label={`Sólo ${data.lab_b.laboratorio}`} value={data.totals.n_only_b} />
                <MiniCard color="rose" label="Ninguno cubre" value={data.totals.n_neither} icon={AlertTriangle} />
              </div>

              {/* Tabla comunes */}
              <div>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h3 className="text-sm font-bold text-slate-950">Moléculas comunes ({data.totals.n_common})</h3>
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none" title="Oculta las moléculas que hoy no compras a ninguno de los dos laboratorios">
                    <input
                      type="checkbox"
                      checked={onlyPurchased}
                      onChange={(e) => setOnlyPurchased(e.target.checked)}
                      data-testid="lab-comparison-only-purchased"
                      className="accent-indigo-600"
                    />
                    Solo las que ya compro a A o B
                    {data.totals.n_common_purchased != null && (
                      <span className="font-mono-num text-slate-400">({data.totals.n_common_purchased})</span>
                    )}
                  </label>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md border border-slate-200 flex-1 max-w-xs ml-auto">
                    <Search className="h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Buscar molécula…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-sm"
                      data-testid="lab-comparison-common-search"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mb-2">
                  Las unidades son las que <strong>ya compras a cada laboratorio</strong> en esa molécula, y el ahorro €/mes se calcula sobre el
                  volumen del laboratorio que pierde (lo que habría que mover al ganador). Pasa el ratón por el € para ver el total del conjunto homogéneo.
                </p>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs" data-testid="lab-comparison-common-table">
                    <thead className="bg-slate-900 text-white">
                      <tr>
                        <th className="text-left px-3 py-2">Molécula (G.H.)</th>
                        <th className="text-right px-3 py-2" title={`Unidades que YA compras a ${data.lab_a.laboratorio} en esta molécula`}>
                          Uds {data.lab_a.laboratorio.split(" ")[0]}
                        </th>
                        <th className="text-right px-3 py-2" title={`Unidades que YA compras a ${data.lab_b.laboratorio} en esta molécula`}>
                          Uds {data.lab_b.laboratorio.split(" ")[0]}
                        </th>
                        <th className="text-right px-3 py-2" title="€/mes que compras hoy a estos dos laboratorios en esta molécula">€/mes</th>
                        <th className="text-right px-3 py-2">% {data.lab_a.laboratorio}</th>
                        <th className="text-right px-3 py-2">% {data.lab_b.laboratorio}</th>
                        <th className="text-right px-3 py-2 text-amber-200">+rappel A</th>
                        <th className="text-right px-3 py-2 text-amber-200">+rappel B</th>
                        <th className="text-left px-3 py-2">Ganador</th>
                        <th className="text-right px-3 py-2">Δ %</th>
                        <th className="text-right px-3 py-2 text-emerald-300" title="Ahorro al mover al ganador lo que hoy compras al laboratorio que pierde">Ahorro €/mes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCommon.length === 0 && (
                        <tr>
                          <td colSpan={11} className="text-center py-8 text-slate-400">
                            {data.common.length === 0
                              ? "No hay moléculas donde ambos ofrezcan condición."
                              : onlyPurchased
                                ? "No compras ninguna de estas moléculas a A ni a B. Desmarca el filtro para ver todas."
                                : "Sin resultados con ese filtro."}
                          </td>
                        </tr>
                      )}
                      {filteredCommon.map((r, i) => {
                        const isA = r.winner === data.lab_a.laboratorio;
                        const rappelA = r.pct_a_with_rappel != null ? r.pct_a_with_rappel : r.pct_a;
                        const rappelB = r.pct_b_with_rappel != null ? r.pct_b_with_rappel : r.pct_b;
                        const hasRappelA = data.lab_a.rappel_enabled && rappelA > r.pct_a;
                        const hasRappelB = data.lab_b.rappel_enabled && rappelB > r.pct_b;
                        return (
                          <tr key={r.gh} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                            <td className="px-3 py-1.5 max-w-[280px] truncate font-medium" title={r.gh}>
                              {r.gh}
                            </td>
                            <td className={`px-3 py-1.5 text-right font-mono-num ${(r.units_a || 0) > 0 ? "" : "text-slate-300"}`}>{r.units_a ?? 0}</td>
                            <td className={`px-3 py-1.5 text-right font-mono-num ${(r.units_b || 0) > 0 ? "" : "text-slate-300"}`}>{r.units_b ?? 0}</td>
                            <td className="px-3 py-1.5 text-right font-mono-num" title={`Total del G.H. en el estudio: ${r.units_total_gh ?? "—"} uds · ${fmtEur(r.eur_month_total_gh || 0)}/mes`}>{fmtEur(r.eur_month)}</td>
                            <td className={`px-3 py-1.5 text-right font-mono-num ${isA ? "font-bold text-emerald-700" : ""}`}>
                              {fmtPct(r.pct_a)}
                            </td>
                            <td className={`px-3 py-1.5 text-right font-mono-num ${!isA ? "font-bold text-emerald-700" : ""}`}>
                              {fmtPct(r.pct_b)}
                            </td>
                            <td className={`px-3 py-1.5 text-right font-mono-num ${hasRappelA ? "text-amber-700 font-semibold" : "text-slate-300"}`}>
                              {hasRappelA ? fmtPct(rappelA) : "—"}
                            </td>
                            <td className={`px-3 py-1.5 text-right font-mono-num ${hasRappelB ? "text-amber-700 font-semibold" : "text-slate-300"}`}>
                              {hasRappelB ? fmtPct(rappelB) : "—"}
                            </td>
                            <td className="px-3 py-1.5">
                              <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                                <Trophy className="h-3 w-3" /> {r.winner}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono-num">{fmtPct(r.delta_pct)}</td>
                            <td className="px-3 py-1.5 text-right font-mono-num text-emerald-700 font-bold">
                              {fmtEur(r.saving_month_eur)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Exclusivas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ExclusiveList
                  title={`Sólo cubre ${data.lab_a.laboratorio}`}
                  rows={data.only_a}
                  pctKey="pct_a"
                  color="blue"
                />
                <ExclusiveList
                  title={`Sólo cubre ${data.lab_b.laboratorio}`}
                  rows={data.only_b}
                  pctKey="pct_b"
                  color="pink"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {transferOpen && transferPlan && (
        <BulkTransferCascadeModal
          fromLabs={[transferPlan.loserLab]}
          allLabs={labs}
          presetToLabs={[transferPlan.winnerLab]}
          restrictGhs={transferPlan.wonGhs}
          restrictLabel={`Traslado sugerido por la comparativa: ${transferPlan.loserLab} → ${transferPlan.winnerLab} (${fmtEur(transferPlan.savingMonthEur)}/mes de ahorro potencial)`}
          newEntriesGhs={transferPlan.newEntriesGhs}
          autoPreview
          onClose={() => setTransferOpen(false)}
          onApplied={() => setTransferOpen(false)}
        />
      )}
    </div>
  );
}

function LabSelect({ label, value, onChange, labs, excluded, disabled, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">{label}</div>
      <select
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
        data-testid={testid}
      >
        <option value="">Selecciona un laboratorio…</option>
        {labs
          .filter((l) => l !== excluded)
          .map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
      </select>
    </div>
  );
}

function LabKpiCard({ k, winners, months, highlight }) {
  const rappelActive = k.rappel_enabled && (k.rappel_extra_pct || 0) > 0;
  return (
    <div className={`rounded-2xl border-2 p-5 ${highlight ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Laboratorio</div>
          <div className="text-xl font-display font-bold text-slate-950">{k.laboratorio}</div>
        </div>
        {highlight && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-800 bg-emerald-100 border border-emerald-300 px-2 py-0.5 rounded-full">
            <Trophy className="h-3 w-3" /> Ganador global
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
        <KpiLine label="% medio efectivo" value={fmtPct(k.avg_effective_pct)} bold />
        <KpiLine label="Descuento lineal (fijo)" value={fmtPct(k.linear_discount_pct)} />
        <KpiLine label="Moléculas cubiertas (estudio)" value={k.n_molecules_covered_in_study} />
        <KpiLine label="Total en vademécum" value={k.n_molecules_covered_total} />
        <KpiLine label="Gana en comunes" value={k.n_wins_in_common} />
        <KpiLine label="€/mes comprado actual" value={fmtEur(k.current_per_month_eur)} />
        <KpiLine label="Convenio activo" value={k.has_agreement ? "Sí" : "No"} />
        <KpiLine label="Aporte ahorro €/mes" value={fmtEur(winners)} bold accent="emerald" />
      </div>
      {rappelActive ? (
        <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-800">
            Rappel {k.rappel_period === "monthly" ? "mensual" : "anual"}
          </div>
          <div className="text-sm text-amber-900 mt-1">
            <strong>+{fmtPct(k.rappel_extra_pct)}</strong> extra si superas {fmtEur(k.rappel_threshold_eur)}
          </div>
          <div className="text-xs text-slate-700 mt-0.5">
            % efectivo con rappel: <strong className="text-emerald-700">{fmtPct(k.avg_effective_pct_with_rappel)}</strong>
          </div>
        </div>
      ) : (
        <div className="mt-3 p-2 rounded-lg bg-slate-50 border border-slate-200 text-[10px] text-slate-500">
          Sin rappel configurado
        </div>
      )}
    </div>
  );
}

function KpiLine({ label, value, bold, accent }) {
  const cls = accent === "emerald" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</span>
      <span className={`font-mono-num mt-0.5 ${bold ? `font-bold ${cls}` : "text-slate-800"}`}>{value}</span>
    </div>
  );
}

function MiniCard({ color, label, value, icon: Icon }) {
  const map = {
    slate: "bg-slate-100 text-slate-800 border-slate-200",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    pink: "bg-pink-50 text-pink-800 border-pink-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${map[color] || map.slate}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-2xl font-mono-num font-bold mt-1">{value}</div>
    </div>
  );
}

function ExclusiveList({ title, rows, pctKey, color }) {
  const map = {
    blue: "border-blue-200 bg-blue-50/40",
    pink: "border-pink-200 bg-pink-50/40",
  };
  return (
    <div className={`rounded-xl border p-4 ${map[color] || ""}`}>
      <h4 className="text-sm font-bold text-slate-950 mb-2">{title} ({rows.length})</h4>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500">No hay moléculas exclusivas.</div>
      ) : (
        <div className="max-h-64 overflow-auto">
          <table className="w-full text-xs">
            <tbody>
              {rows.slice(0, 200).map((r) => (
                <tr key={r.gh} className="border-b border-slate-200/60">
                  <td className="py-1.5 pr-2 max-w-[280px] truncate" title={r.gh}>{r.gh}</td>
                  <td className="py-1.5 pr-2 text-right font-mono-num text-slate-500" title={`Lo que compras hoy a estos dos laboratorios. Total del G.H.: ${fmtEur(r.eur_month_total_gh || 0)}/mes`}>
                    {fmtEur(r.eur_month)}/mes
                  </td>
                  <td className="py-1.5 text-right font-mono-num font-semibold text-emerald-700">{fmtPct(r[pctKey])}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 200 && (
            <div className="text-[10px] text-slate-500 mt-2">
              Mostrando 200 de {rows.length}. Descarga Excel para el listado completo.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
